// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Group, Modal, Select, Stack } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { JSX } from 'react';

interface AppointmentCancellationModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  patientName?: string;
  reason?: string | null;
  onReasonChange?: (reason: string | null) => void;
  loading?: boolean;
  reasonInput?: JSX.Element;
  confirmLabel?: string;
}

const REASONS = [
  { value: 'Patient request', label: 'Patient request' },
  { value: 'Provider unavailable', label: 'Provider unavailable' },
  { value: 'Duplicate booking', label: 'Duplicate booking' },
  { value: 'No longer needed', label: 'No longer needed' },
  { value: 'Other', label: 'Other' },
];

export function AppointmentCancellationModal({
  opened,
  onClose,
  onConfirm,
  patientName,
  reason,
  onReasonChange,
  loading = false,
  reasonInput,
  confirmLabel = 'Confirm Cancellation',
}: AppointmentCancellationModalProps): JSX.Element {
  return (
    <Modal opened={opened} onClose={onClose} centered title="Cancel appointment?" size="md" padding="xl" radius="md">
      <Stack gap="lg">
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={20} />}>
          Are you sure you want to cancel this appointment{patientName ? ` for ${patientName}` : ''}? This action cannot
          be undone.
        </Alert>
        {reasonInput}
        {onReasonChange && (
          <Select
            label="Reason for cancellation"
            placeholder="Select a reason"
            data={REASONS}
            value={reason ?? null}
            onChange={onReasonChange}
            withAsterisk
            allowDeselect={false}
          />
        )}
        <Group grow wrap="nowrap">
          <Button variant="default" size="md" onClick={onClose} disabled={loading} style={{ whiteSpace: 'nowrap' }}>
            No, keep
          </Button>
          <Button
            color="red"
            size="md"
            onClick={onConfirm}
            loading={loading}
            style={{ whiteSpace: 'nowrap', minWidth: 'max-content' }}
          >
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
